import { useCallback, type RefObject } from "react";
import type { Breakpoint, SxProps, Theme } from "@mui/material";
import { useResponsiveView } from "@gui/hooks/useResponsiveView";
import type { AppDialogProps, AppDialogSize } from "./AppDialog.types";

const MAX_WIDTH: Record<AppDialogSize, Breakpoint> = { confirm: "xs", form: "sm", wide: "xl" };

const FILL_PAPER_SX: SxProps<Theme> = { width: "90vw", maxWidth: "90vw", height: "90vh", maxHeight: "90vh" };

interface AppDialogRefs {
	primary: RefObject<HTMLButtonElement | null>;
	cancel: RefObject<HTMLButtonElement | null>;
}

export function useAppDialogViewModel(props: AppDialogProps, refs?: AppDialogRefs) {
	const { isMobile } = useResponsiveView();
	const closeDisabled = props.closeDisabled ?? false;
	const onClose = props.onClose;
	const handleClose = useCallback(() => {
		if (!closeDisabled) onClose();
	}, [closeDisabled, onClose]);

	// On a phone a form or wide dialog takes the whole screen, and its header row carries the ✕
	// (in place of Cancel) and the primary action, where a thumb and the soft keyboard leave them reachable.
	const fullScreen = isMobile && props.size !== "confirm";
	const hasPrimary = props.primaryAction !== undefined;

	const cancelLabel = props.cancelLabel === undefined ? "Cancel" : props.cancelLabel;
	// A dialog with nothing to do but look is closed with a ✕ beside its title, as is every full-screen one.
	const showHeaderClose = fullScreen || !hasPrimary;
	const showCancel = !fullScreen && hasPrimary && cancelLabel !== null;
	const showFooter = fullScreen ? props.secondaryActions != null : hasPrimary || props.secondaryActions != null;
	// With no Cancel to start on, focus starts on the only button there is.
	const focusPrimary = props.initialFocus !== "cancel" || !showCancel;

	// Focus is placed as the dialog starts to open rather than with `autoFocus`: a dialog opened over
	// a menu or another dialog mounts before it is the top modal, so the modal underneath would pull
	// an autoFocused button's focus back, and this dialog would then settle on its bare container.
	// A form or wide dialog is left to MUI, which focuses the dialog itself: starting on its primary
	// action would let a stray Enter submit a form nobody has looked at yet.
	const placesFocus = props.size === "confirm" || props.initialFocus !== undefined;
	const handleEntering = useCallback(() => {
		if (!placesFocus) return;
		(focusPrimary ? refs?.primary : refs?.cancel)?.current?.focus();
	}, [placesFocus, focusPrimary, refs?.primary, refs?.cancel]);

	return {
		handleClose,
		closeDisabled,
		cancelLabel,
		maxWidth: MAX_WIDTH[props.size],
		fullScreen,
		paperSx: props.fill && !fullScreen ? FILL_PAPER_SX : undefined,
		showHeaderClose,
		/** The ✕ leads the header row on a phone and ends it on desktop, as it did before AppDialog. */
		headerCloseFirst: fullScreen,
		/** The primary action sits in the header row on a phone, in the footer otherwise. */
		primaryInHeader: fullScreen && hasPrimary,
		showCancel,
		showFooter,
		focusPrimary,
		handleEntering,
	};
}
